//Dependencies
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { IonicApp, IonicModule } from 'ionic-angular';
import { SplashScreen } from '@ionic-native/splash-screen';
import { StatusBar } from '@ionic-native/status-bar';
import { HttpClientModule, HttpClient } from '@angular/common/http';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { IonicStorageModule } from '@ionic/storage';
import { RestangularModule } from "ngx-restangular";
import { TooltipModule } from "ngx-tooltip";
import { OneSignal } from "@ionic-native/onesignal";
import { Network } from "@ionic-native/network";
import { BrowserTab } from "@ionic-native/browser-tab";
import { Keyboard } from "@ionic-native/keyboard";
import { NgSelectModule } from '@ng-select/ng-select';
import { DatePickerModule } from 'ion-datepicker';

declare const buildConfig: any;

//Loaders
export function TranslateLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/translations/', '.json?v='+buildConfig.BUILD_DATE);
}

import { RestangularConfigFactory } from "../helpers/restangular.config.factory";




//Helpers
import { IonicHelper } from "../helpers/ionic.helper";
import { NavigationHelper } from "../helpers/navigation.helper";
import { FormHelper } from "../helpers/form.helper";

//Providers / Services
import { AuthService } from "../services/auth.service";
import { ApiService } from "../services/api.service";
import { UserGroupService } from "../services/user-group.service";
import { EventService } from "../services/event.service";
import { SearchService } from "../services/search.service";
import { NotificationService } from "../services/notification.service";


//Routing
import { ROUTES } from './app.routes';

//Pipes & Directives
import { ObjKeysPipe } from "../pipes/objKeys";
import { DateTimePipe } from "../pipes/dateTime";
import { Plurality } from "../pipes/plurality";
import { ObjectifyPipe } from "../pipes/objectify";
import { ClickOutsideDirective } from "../directives/click.outside.directive";
import { NumSelectedPipe } from "../pipes/numSelected";
import { ImageUrlPipe } from "../pipes/imageUrl";
import { FileUrlPipe } from "../pipes/fileUrl";


//Components
import { Cas } from './app.component';
import { Dashboard } from "../components/dashboard/dashboard";
import { Login } from '../components/login/login/login';
import { ForgotPassword } from '../components/login/forgot-password/forgot-password';
import { ResetPassword } from "../components/login/reset-password/reset-password";
import { AcceptInvitation } from "../components/login/accept-invitation/accept-invitation";
import { Register } from "../components/login/register/register";
import { SinglePost } from "../components/single-post/single-post";
import { Profile } from "../components/profile/profile";
import { EditProfile } from "../components/edit-profile/edit-profile";
import { Search } from "../components/search/search";
import { AdminPanel } from "../components/admin-panel/admin-panel";
import { NotificationsPage } from "../components/notifications-page/notifications-page";
import { NotificationSettings } from "../components/notification-settings/notification-settings";

//Partials
import { PostList } from '../partials/post/post-list/post-list';
import { PostCard } from "../partials/post/post-card/post-card";
import { PostAuthor } from "../partials/post/post-author/post-author";
import { PostCreate } from "../partials/post/post-create/post-create";
import { TinyMce } from "../partials/tiny-mce/tiny-mce";
import { Icon } from "../partials/icon/icon";
import { UserGroupTag } from "../partials/user-group/user-group-tag/user-group-tag";
import { UserGroupList } from "../partials/user-group/user-group-list/user-group-list";
import { HeaderBar } from "../partials/header/header-bar/header-bar";
import { PostListContainer } from "../partials/post/post-list-container/post-list-container";
import { UserStatistics } from "../partials/user/user-statistics/user-statistics";
import { ReactionCard } from "../partials/reaction/reaction-card/reaction-card";
import { ReactionCreate } from "../partials/reaction/reaction-create/reaction-create";
import { ReactionEdit } from "../partials/reaction/reaction-edit/reaction-edit";
import { BackButton } from "../partials/back-button/back-button";
import { SearchField } from "../partials/search-field/search-field";
import { CustomNavLink } from "../partials/nav-link/nav-link";
import { UserNavigation } from "../partials/user/user-navigation/user-navigation";
import { UserGroupSidebar } from "../partials/user-group/user-group-sidebar/user-group-sidebar";
import { HeaderBarMobile } from "../partials/header/header-bar-mobile/header-bar-mobile";
import { TabBar } from "../partials/tab-bar/tab-bar";
import { Avatar } from "../partials/user/avatar/avatar";
import { UserService } from "../services/user.service";
import { PageTitleService } from "../services/page-title.service";
import { UserGroupTagList } from "../partials/user-group/user-group-tag-list/user-group-tag-list";
import { MobileSidebar } from "../partials/mobile-sidebar/mobile-sidebar";
import { PostFilterList } from "../partials/post/post-filter-list/post-filter-list";
import { PostEdit } from "../partials/post/post-edit/post-edit";
import { UserCard } from "../partials/user/user-card/user-card";
import { ImageUpload } from "../partials/upload/image-upload";
import { NotificationList } from "../partials/notification/notification-list/notification-list";
import { NotificationCard } from "../partials/notification/notification-card/notification-card";
import { AdminUserGroup } from "../partials/admin/admin-user-group/admin-user-group";
import { AdminUserGroupCreate } from "../partials/admin/admin-user-group-create/admin-user-group-create";
import { AdminUser } from "../partials/admin/admin-user/admin-user";
import { ProfileForm } from "../partials/user/profile-form/profile-form";
import { AdminUserCreate } from "../partials/admin/admin-user-create/admin-user-create";
import { ReportCreate } from "../partials/report-create/report-create";
import { AdminNavigation } from "../partials/admin/admin-navigation/admin-navigation";
import { FileUpload } from "../partials/upload/file-upload";
import { Attachment } from "../partials/upload/attachment";
import { ContentEditable } from "../partials/contenteditable/contenteditable";
import { AppErrorHandler } from "./app.error-handler";
import { IosEditor } from "../partials/ios-editor/ios-editor";
import { UserGroupDropDown } from '../partials/user-group/user-group-drop-down/user-group-drop-down';
import { AdminCategoryCreate } from '../partials/admin/admin-category-create/admin-category-create';
import { AdminCategory } from '../partials/admin/admin-category/admin-category';
import { CategoryService } from '../services/category.service';
import { PostService } from "../services/post.service";
import { CategoryList } from '../partials/category/category-list/category-list';
import { CategoryDropDown } from '../partials/category/category-drop-down/category-drop-down';
import { TagService } from '../services/tag.service';
import { TagSelector } from '../partials/tags/tag-selector/tag-selector';
import { FormsModule } from '@angular/forms';
import { TagList } from '../partials/tags/tag-list/tag-list';
import { AdminTag } from '../partials/admin/admin-tag/admin-tag';
import { FeedbackModal } from '../partials/feedback-modal/feedback-modal';
import { AdminUserInvite } from '../partials/admin/admin-user-invite/admin-user-invite';
import { ChatPage } from '../components/chat-page/chat-page';
import { ChatService } from '../services/chat.service';
import { registerLocaleData } from '@angular/common';
import nlLocale from '@angular/common/locales/nl'
import { PostTaxonomies } from "../partials/post/post-taxonomies/post-taxonomies";
import { PostCategoryList } from "../partials/category/post-category-list/post-category-list";
import { TaxSearchComponent } from "../components/tax-search/tax-search.component";
import { ChatAdminPage } from '../components/chat-admin-page/chat-admin-page';
import { Dropdown } from '../partials/dropdown/dropdown';

const components = [
  Cas,
  Dashboard,
  Login,
  ForgotPassword,
  ResetPassword,
  AcceptInvitation,
  Register,
  PostListContainer,
  SinglePost,
	Search,
	Profile,
	AdminPanel,
	EditProfile,
	NotificationsPage,
  NotificationSettings,
  ChatPage,
  ChatAdminPage
];

const partials = [
  CategoryList,
  Dropdown,
  PostList,
  PostCard,
  PostAuthor,
  PostCreate,
  PostEdit,
  ReactionCard,
	ReactionCreate,
	ReactionEdit,
	ReportCreate,
  UserGroupTag,
  UserGroupList,
  HeaderBar,
  HeaderBarMobile,
  TabBar,
  Avatar,
  Icon,
  UserStatistics,
	UserNavigation,
  TinyMce,
  ContentEditable,
  IosEditor,
  BackButton,
  SearchField,
	CustomNavLink,
  UserGroupSidebar,
  UserGroupTagList,
	MobileSidebar,
	PostFilterList,
  UserCard,
	ProfileForm,
  FileUpload,
  ImageUpload,
	NotificationList,
	NotificationCard,
	AdminUserGroup,
	AdminUserGroupCreate,
	AdminUserCreate,
	AdminUser,
  AdminUserInvite,
	AdminNavigation,
  Attachment,
  AdminCategoryCreate,
  AdminCategory,
  AdminTag,
  UserGroupDropDown,
  CategoryDropDown,
  TagSelector,
  TagList,
  FeedbackModal,
  PostTaxonomies,
  PostCategoryList,
  TaxSearchComponent
];

const pipes = [
  ObjKeysPipe,
  DateTimePipe,
  Plurality,
  ObjectifyPipe,
  ClickOutsideDirective,
  NumSelectedPipe,
  ImageUrlPipe,
  FileUrlPipe
];

const services = [
  { provide: ErrorHandler, useClass: AppErrorHandler },
  OneSignal,
  StatusBar,
  Keyboard,
  BrowserTab,
  SplashScreen,
  IonicHelper,
  NavigationHelper,
  FormHelper,
  AuthService,
  ApiService,
  UserGroupService,
  EventService,
  UserService,
  PageTitleService,
  SearchService,
  NotificationService,
  PostService,
  Network,
  CategoryService,
  TagService,
  ChatService,
  { provide: LOCALE_ID, useValue: "nl" }
];


registerLocaleData(nlLocale);

@NgModule({
  declarations: [...components, ...partials, ...pipes],
  entryComponents: components,
  providers: services,
  imports: [
    NgSelectModule,
    DatePickerModule,
    FormsModule,
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    TooltipModule,
    RestangularModule.forRoot([AuthService], RestangularConfigFactory),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: (TranslateLoaderFactory),
        deps: [HttpClient]
      }
    }),
    IonicStorageModule.forRoot(),
    IonicModule.forRoot(Cas, {
      platforms: {
        ios: {
          swipeBackEnabled: true
        }
      }

    }
    , ROUTES)
  ],
  bootstrap: [IonicApp],
})
export class AppModule {}



// WEBPACK FOOTER //
// ./src/app/app.module.ts